import { requireSupabaseClient } from "../../lib/supabase";
import { convertUploadToWebp } from "../../lib/image-webp";
import type {
  CouncilMember,
  MayorProfile,
  NewsItem,
  PublicPageContent,
  SiteMedia,
  TouristAttraction,
  TouristAttractionCategory,
} from "../../types/domain";
import {
  defaultTypographySettings,
  normalizeFontChoice,
  type TypographySettings,
} from "./visual-identity";

type Row = Record<string, unknown>;

export interface ManagedNews extends Omit<NewsItem, "relatedFiles"> {
  status: "draft" | "published" | "archived";
  imagePath: string | null;
}

export interface NewsEditorInput {
  id?: string;
  municipalityId: string;
  slug: string;
  title: string;
  titleGn: string;
  excerpt: string;
  excerptGn: string;
  body: string;
  bodyGn: string;
  category: string;
  categoryGn: string;
  status: ManagedNews["status"];
  publishedAt: string;
  importance: number;
  imageAlt: string;
  imageAltGn: string;
  imageCredit: string;
  sourceUrl: string;
  previousImagePath?: string | null;
}

export interface SiteMediaInput {
  municipalityId: string;
  slotCode: string;
  label: string;
  labelGn: string;
  altText: string;
  altTextGn: string;
  credit: string;
  description: string;
  descriptionGn: string;
  displayOrder: number;
  active: boolean;
  focalX: number;
  focalY: number;
  previousStoragePath?: string | null;
}

export interface MayorProfileInput extends Omit<MayorProfile, "id"> {
  municipalityId: string;
}

export interface CouncilMemberInput {
  id?: string;
  municipalityId: string;
  fullName: string;
  positionTitle: string;
  positionTitleGn: string;
  affiliation: string;
  affiliationGn: string;
  displayOrder: number;
  published: boolean;
  officiallyProclaimed: boolean;
  sourceUrl: string;
  sourceLabel: string;
  sourceLabelGn: string;
  sourceCheckedOn: string;
  imageAlt: string;
  imageAltGn: string;
  imageCredit: string;
  previousImagePath?: string | null;
}

export interface PageContentInput extends Omit<PublicPageContent, "id"> {
  municipalityId: string;
}

export interface TouristAttractionInput {
  id?: string;
  municipalityId: string;
  slug: string;
  name: string;
  nameGn: string;
  category: TouristAttractionCategory;
  shortDescription: string;
  shortDescriptionGn: string;
  location: string;
  locationGn: string;
  mapUrl: string;
  latitude: number | null;
  longitude: number | null;
  imageAlt: string;
  imageAltGn: string;
  imageCredit: string;
  sourceUrl: string;
  displayOrder: number;
  published: boolean;
  previousImagePath?: string | null;
}

function row(value: unknown): Row {
  return value && typeof value === "object" ? (value as Row) : {};
}

function rows(value: unknown): Row[] {
  return Array.isArray(value) ? value.map(row) : [];
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function numberValue(value: unknown): number {
  return typeof value === "number" ? value : Number(value) || 0;
}

function publicUrl(path: string): string {
  return requireSupabaseClient().storage.from("public-content").getPublicUrl(path).data
    .publicUrl;
}

function validateImage(file: File): void {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    throw new Error("La imagen debe ser JPG, PNG o WebP.");
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("La imagen no puede superar 10 MB.");
  }
}

async function uploadImage(
  municipalityId: string,
  folder: "news" | "site" | "tourism" | "council",
  file: File,
): Promise<string> {
  validateImage(file);
  const optimizedFile = await convertUploadToWebp(file);
  const path = municipalityId + "/" + folder + "/" + crypto.randomUUID() + ".webp";
  const { error } = await requireSupabaseClient()
    .storage.from("public-content")
    .upload(path, optimizedFile, { contentType: "image/webp", upsert: false });
  if (error) {
    throw new Error(`No se pudo subir la imagen: ${error.message}`);
  }
  return path;
}

async function removeImage(path?: string | null): Promise<void> {
  if (!path) return;
  await requireSupabaseClient().storage.from("public-content").remove([path]);
}

function mapMedia(item: Row): SiteMedia {
  const storagePath = text(item.storage_path);
  return {
    id: text(item.id),
    slotCode: text(item.slot_code),
    label: text(item.label),
    labelGn: text(item.label_gn),
    storagePath,
    imageUrl: publicUrl(storagePath),
    altText: text(item.alt_text),
    altTextGn: text(item.alt_text_gn),
    credit: text(item.credit),
    active: item.active === true,
    description: text(item.description),
    descriptionGn: text(item.description_gn),
    displayOrder: numberValue(item.display_order) || 1,
    focalX:
      item.focal_x === null || item.focal_x === undefined
        ? 50
        : numberValue(item.focal_x),
    focalY:
      item.focal_y === null || item.focal_y === undefined
        ? 50
        : numberValue(item.focal_y),
  };
}

function mapMayorProfile(item: Row): MayorProfile {
  return {
    id: text(item.id),
    fullName: text(item.full_name),
    officeTitle: text(item.office_title),
    officeTitleGn: text(item.office_title_gn),
    profileTitle: text(item.profile_title),
    profileTitleGn: text(item.profile_title_gn),
    biography: text(item.biography),
    biographyGn: text(item.biography_gn),
    institutionalDescription: text(item.institutional_description),
    institutionalDescriptionGn: text(item.institutional_description_gn),
    published: item.published === true,
  };
}

function mapManagedNews(item: Row): ManagedNews {
  const imagePath = text(item.image_path) || null;
  return {
    id: text(item.id),
    slug: text(item.slug),
    title: text(item.title),
    titleGn: text(item.title_gn),
    excerpt: text(item.excerpt),
    excerptGn: text(item.excerpt_gn),
    body: text(item.body),
    bodyGn: text(item.body_gn),
    category: text(item.category),
    categoryGn: text(item.category_gn),
    publishedAt: text(item.published_at),
    importance: numberValue(item.importance),
    imagePath,
    imageUrl: imagePath ? publicUrl(imagePath) : null,
    imageAlt: text(item.image_alt),
    imageAltGn: text(item.image_alt_gn),
    imageCredit: text(item.image_credit),
    sourceUrl: text(item.source_url) || null,
    status: text(item.status) as ManagedNews["status"],
  };
}

function mapCouncilMember(item: Row): CouncilMember {
  const imagePath = text(item.image_path) || null;
  return {
    id: text(item.id),
    fullName: text(item.full_name),
    positionTitle: text(item.position_title),
    positionTitleGn: text(item.position_title_gn),
    affiliation: text(item.affiliation),
    affiliationGn: text(item.affiliation_gn),
    displayOrder: numberValue(item.display_order),
    published: item.published === true,
    officiallyProclaimed: item.officially_proclaimed === true,
    sourceUrl: text(item.source_url) || null,
    sourceLabel: text(item.source_label),
    sourceLabelGn: text(item.source_label_gn),
    sourceCheckedOn: text(item.source_checked_on) || null,
    imagePath,
    imageUrl: imagePath ? publicUrl(imagePath) : null,
    imageAlt: text(item.image_alt),
    imageAltGn: text(item.image_alt_gn),
    imageCredit: text(item.image_credit),
  };
}

function mapPageContent(item: Row): PublicPageContent {
  return {
    id: text(item.id),
    pageCode: text(item.page_code) as PublicPageContent["pageCode"],
    eyebrow: text(item.eyebrow),
    eyebrowGn: text(item.eyebrow_gn),
    title: text(item.title),
    titleGn: text(item.title_gn),
    summary: text(item.summary),
    summaryGn: text(item.summary_gn),
    body: text(item.body),
    bodyGn: text(item.body_gn),
    published: item.published === true,
  };
}

function mapTouristAttraction(item: Row): TouristAttraction {
  const imagePath = text(item.image_path) || null;
  return {
    id: text(item.id),
    slug: text(item.slug),
    name: text(item.name),
    nameGn: text(item.name_gn),
    category: text(item.category) as TouristAttractionCategory,
    shortDescription: text(item.short_description),
    shortDescriptionGn: text(item.short_description_gn),
    location: text(item.location),
    locationGn: text(item.location_gn),
    mapUrl: text(item.map_url) || null,
    latitude: item.latitude === null || item.latitude === undefined
      ? null
      : numberValue(item.latitude),
    longitude: item.longitude === null || item.longitude === undefined
      ? null
      : numberValue(item.longitude),
    imagePath,
    imageUrl: imagePath ? publicUrl(imagePath) : null,
    imageAlt: text(item.image_alt),
    imageAltGn: text(item.image_alt_gn),
    imageCredit: text(item.image_credit),
    sourceUrl: text(item.source_url) || null,
    displayOrder: numberValue(item.display_order),
    published: item.published === true,
  };
}

export const contentApi = {
  async listPublicMayorProfiles(): Promise<MayorProfile[]> {
    const { data, error } = await requireSupabaseClient()
      .from("mayor_profiles")
      .select("id, full_name, office_title, office_title_gn, profile_title, profile_title_gn, biography, biography_gn, institutional_description, institutional_description_gn, published, municipalities!inner(code)")
      .eq("municipalities.code", "PBY")
      .eq("published", true);
    if (error) throw new Error("No se pudo cargar la ficha del Intendente.");
    return rows(data).map(mapMayorProfile);
  },

  async listManagedMayorProfiles(municipalityId: string): Promise<MayorProfile[]> {
    const { data, error } = await requireSupabaseClient()
      .from("mayor_profiles")
      .select("id, full_name, office_title, office_title_gn, profile_title, profile_title_gn, biography, biography_gn, institutional_description, institutional_description_gn, published")
      .eq("municipality_id", municipalityId);
    if (error) throw new Error("No se pudo cargar la ficha administrada del Intendente.");
    return rows(data).map(mapMayorProfile);
  },

  async saveMayorProfile(input: MayorProfileInput, userId: string): Promise<void> {
    const { error } = await requireSupabaseClient().from("mayor_profiles").upsert({
      municipality_id: input.municipalityId,
      full_name: input.fullName,
      office_title: input.officeTitle,
      office_title_gn: input.officeTitleGn,
      profile_title: input.profileTitle,
      profile_title_gn: input.profileTitleGn,
      biography: input.biography,
      biography_gn: input.biographyGn,
      institutional_description: input.institutionalDescription,
      institutional_description_gn: input.institutionalDescriptionGn,
      published: input.published,
      updated_by: userId,
      created_by: userId,
    }, { onConflict: "municipality_id" });
    if (error) throw new Error("No se pudo guardar la ficha del Intendente.");
  },

  async getCarouselInterval(): Promise<number> {
    const { data, error } = await requireSupabaseClient()
      .from("system_settings")
      .select("value, municipalities!inner(code)")
      .eq("municipalities.code", "PBY")
      .eq("key", "home_carousel_interval_seconds")
      .eq("is_public", true)
      .maybeSingle();
    if (error) return 3;
    const value = Number(row(data).value);
    return Number.isFinite(value) && value >= 2 && value <= 10 ? value : 3;
  },

  async getTypographySettings(): Promise<TypographySettings> {
    const { data, error } = await requireSupabaseClient()
      .from("system_settings")
      .select("key, value, municipalities!inner(code)")
      .eq("municipalities.code", "PBY")
      .eq("is_public", true)
      .in("key", ["site_font_heading", "site_font_subtitle", "site_font_body"]);
    if (error) return defaultTypographySettings;
    const settings = new Map(
      rows(data).map((item) => [text(item.key), item.value]),
    );
    return {
      heading: normalizeFontChoice(
        settings.get("site_font_heading"),
        defaultTypographySettings.heading,
      ),
      subtitle: normalizeFontChoice(
        settings.get("site_font_subtitle"),
        defaultTypographySettings.subtitle,
      ),
      body: normalizeFontChoice(
        settings.get("site_font_body"),
        defaultTypographySettings.body,
      ),
    };
  },

  async saveTypographySettings(
    municipalityId: string,
    settings: TypographySettings,
    userId: string,
  ): Promise<void> {
    const values = [
      ["site_font_heading", settings.heading],
      ["site_font_subtitle", settings.subtitle],
      ["site_font_body", settings.body],
    ].map(([key, value]) => ({
      municipality_id: municipalityId,
      key,
      value,
      is_public: true,
      updated_by: userId,
    }));
    const { error } = await requireSupabaseClient()
      .from("system_settings")
      .upsert(values, { onConflict: "municipality_id,key" });
    if (error) throw new Error("No se pudo guardar la tipografía del portal.");
  },

  async saveCarouselInterval(municipalityId: string, seconds: number, userId: string): Promise<void> {
    const normalized = Math.min(10, Math.max(2, Math.round(seconds)));
    const { error } = await requireSupabaseClient().from("system_settings").upsert({
      municipality_id: municipalityId,
      key: "home_carousel_interval_seconds",
      value: normalized,
      is_public: true,
      updated_by: userId,
    }, { onConflict: "municipality_id,key" });
    if (error) throw new Error("No se pudo guardar el intervalo del carrusel.");
  },
  async listPublicPageContent(): Promise<PublicPageContent[]> {
    const { data, error } = await requireSupabaseClient()
      .from("public_page_content")
      .select(
        "id, page_code, eyebrow, eyebrow_gn, title, title_gn, summary, summary_gn, body, body_gn, published, municipalities!inner(code)",
      )
      .eq("municipalities.code", "PBY")
      .eq("published", true);
    if (error) throw new Error("No se pudieron cargar los textos públicos.");
    return rows(data).map(mapPageContent);
  },

  async listManagedPageContent(municipalityId: string): Promise<PublicPageContent[]> {
    const { data, error } = await requireSupabaseClient()
      .from("public_page_content")
      .select(
        "id, page_code, eyebrow, eyebrow_gn, title, title_gn, summary, summary_gn, body, body_gn, published",
      )
      .eq("municipality_id", municipalityId)
      .order("page_code");
    if (error) throw new Error("No se pudieron cargar los textos administrados.");
    return rows(data).map(mapPageContent);
  },

  async savePageContent(input: PageContentInput, userId: string): Promise<void> {
    const { error } = await requireSupabaseClient().from("public_page_content").upsert(
      {
        municipality_id: input.municipalityId,
        page_code: input.pageCode,
        eyebrow: input.eyebrow,
        eyebrow_gn: input.eyebrowGn,
        title: input.title,
        title_gn: input.titleGn,
        summary: input.summary,
        summary_gn: input.summaryGn,
        body: input.body,
        body_gn: input.bodyGn,
        published: input.published,
        updated_by: userId,
      },
      { onConflict: "municipality_id,page_code" },
    );
    if (error) throw new Error("No se pudo guardar el texto público.");
  },

  async listPublicCouncilMembers(): Promise<CouncilMember[]> {
    const { data, error } = await requireSupabaseClient()
      .from("council_members")
      .select(
        "id, full_name, position_title, position_title_gn, affiliation, affiliation_gn, display_order, published, officially_proclaimed, source_url, source_label, source_label_gn, source_checked_on, image_path, image_alt, image_alt_gn, image_credit, municipalities!inner(code)",
      )
      .eq("municipalities.code", "PBY")
      .eq("published", true)
      .order("display_order");
    if (error) throw new Error("No se pudo cargar la Junta Municipal.");
    return rows(data).map(mapCouncilMember);
  },

  async listManagedCouncilMembers(municipalityId: string): Promise<CouncilMember[]> {
    const { data, error } = await requireSupabaseClient()
      .from("council_members")
      .select(
        "id, full_name, position_title, position_title_gn, affiliation, affiliation_gn, display_order, published, officially_proclaimed, source_url, source_label, source_label_gn, source_checked_on, image_path, image_alt, image_alt_gn, image_credit",
      )
      .eq("municipality_id", municipalityId)
      .order("display_order");
    if (error) throw new Error("No se pudo cargar la nómina administrada.");
    return rows(data).map(mapCouncilMember);
  },

  async saveCouncilMember(
    input: CouncilMemberInput,
    userId: string,
    image?: File,
  ): Promise<void> {
    const imagePath = image
      ? await uploadImage(input.municipalityId, "council", image)
      : input.previousImagePath ?? null;
    const values = {
      municipality_id: input.municipalityId,
      full_name: input.fullName,
      position_title: input.positionTitle,
      position_title_gn: input.positionTitleGn,
      affiliation: input.affiliation || null,
      affiliation_gn: input.affiliationGn || null,
      display_order: input.displayOrder,
      published: input.published,
      officially_proclaimed: input.officiallyProclaimed,
      source_url: input.sourceUrl || null,
      source_label: input.sourceLabel || null,
      source_label_gn: input.sourceLabelGn || null,
      source_checked_on: input.sourceCheckedOn || null,
      image_path: imagePath,
      image_alt: input.imageAlt || null,
      image_alt_gn: input.imageAltGn || null,
      image_credit: input.imageCredit || null,
      updated_by: userId,
    };
    const query = input.id
      ? requireSupabaseClient()
          .from("council_members")
          .update(values)
          .eq("id", input.id)
      : requireSupabaseClient()
          .from("council_members")
          .insert({ ...values, created_by: userId });
    const { error } = await query;
    if (error) {
      if (image && imagePath) await removeImage(imagePath);
      throw new Error("No se pudo guardar el integrante de la Junta.");
    }
    if (image && input.previousImagePath && input.previousImagePath !== imagePath) {
      await removeImage(input.previousImagePath);
    }
  },

  async deleteCouncilMember(id: string, imagePath?: string | null): Promise<void> {
    const { error } = await requireSupabaseClient()
      .from("council_members")
      .delete()
      .eq("id", id);
    if (error) throw new Error("No se pudo eliminar el integrante de la Junta.");
    await removeImage(imagePath);
  },

  async listPublicTouristAttractions(): Promise<TouristAttraction[]> {
    const { data, error } = await requireSupabaseClient()
      .from("tourist_attractions")
      .select(
        "id, slug, name, name_gn, category, short_description, short_description_gn, location, location_gn, map_url, latitude, longitude, image_path, image_alt, image_alt_gn, image_credit, source_url, display_order, published, municipalities!inner(code)",
      )
      .eq("municipalities.code", "PBY")
      .eq("published", true)
      .order("display_order");
    if (error) throw new Error("No se pudieron cargar los lugares turísticos.");
    return rows(data).map(mapTouristAttraction);
  },

  async listManagedTouristAttractions(
    municipalityId: string,
  ): Promise<TouristAttraction[]> {
    const { data, error } = await requireSupabaseClient()
      .from("tourist_attractions")
      .select(
        "id, slug, name, name_gn, category, short_description, short_description_gn, location, location_gn, map_url, latitude, longitude, image_path, image_alt, image_alt_gn, image_credit, source_url, display_order, published",
      )
      .eq("municipality_id", municipalityId)
      .order("display_order");
    if (error) throw new Error("No se pudo cargar el catálogo turístico.");
    return rows(data).map(mapTouristAttraction);
  },

  async saveTouristAttraction(
    input: TouristAttractionInput,
    userId: string,
    image?: File,
  ): Promise<void> {
    const newPath = image
      ? await uploadImage(input.municipalityId, "tourism", image)
      : input.previousImagePath || null;
    const values = {
      municipality_id: input.municipalityId,
      slug: input.slug,
      name: input.name,
      name_gn: input.nameGn,
      category: input.category,
      short_description: input.shortDescription,
      short_description_gn: input.shortDescriptionGn,
      location: input.location,
      location_gn: input.locationGn,
      map_url: input.mapUrl || null,
      latitude: input.latitude,
      longitude: input.longitude,
      image_path: newPath,
      image_alt: newPath ? input.imageAlt : null,
      image_alt_gn: newPath ? input.imageAltGn : null,
      image_credit: input.imageCredit || null,
      source_url: input.sourceUrl || null,
      display_order: input.displayOrder,
      published: input.published,
      updated_by: userId,
    };
    const query = input.id
      ? requireSupabaseClient()
          .from("tourist_attractions")
          .update(values)
          .eq("id", input.id)
      : requireSupabaseClient()
          .from("tourist_attractions")
          .insert({ ...values, created_by: userId });
    const { error } = await query;
    if (error) {
      if (image) await removeImage(newPath);
      throw new Error("No se pudo guardar el lugar turístico.");
    }
    if (image && input.previousImagePath && input.previousImagePath !== newPath) {
      await removeImage(input.previousImagePath);
    }
  },

  async deleteTouristAttraction(item: TouristAttraction): Promise<void> {
    const { error } = await requireSupabaseClient()
      .from("tourist_attractions")
      .delete()
      .eq("id", item.id);
    if (error) throw new Error("No se pudo eliminar el lugar turístico.");
    await removeImage(item.imagePath);
  },

  async listPublicMedia(): Promise<SiteMedia[]> {
    const { data, error } = await requireSupabaseClient()
      .from("site_media")
      .select(
        "id, slot_code, label, label_gn, storage_path, alt_text, alt_text_gn, credit, active, description, description_gn, display_order, focal_x, focal_y, municipalities!inner(code)",
      )
      .eq("municipalities.code", "PBY")
      .eq("active", true)
      .order("display_order");
    if (error) throw new Error("No se pudieron cargar las imágenes institucionales.");
    return rows(data).map(mapMedia);
  },

  async listManagedMedia(municipalityId: string): Promise<SiteMedia[]> {
    const { data, error } = await requireSupabaseClient()
      .from("site_media")
      .select(
        "id, slot_code, label, label_gn, storage_path, alt_text, alt_text_gn, credit, active, description, description_gn, display_order, focal_x, focal_y",
      )
      .eq("municipality_id", municipalityId)
      .order("slot_code");
    if (error) throw new Error("No se pudo cargar la biblioteca visual.");
    return rows(data).map(mapMedia);
  },

  async saveMedia(input: SiteMediaInput, userId: string, file: File): Promise<void> {
    const newPath = await uploadImage(input.municipalityId, "site", file);
    const values = {
      municipality_id: input.municipalityId,
      slot_code: input.slotCode,
      label: input.label,
      label_gn: input.labelGn,
      storage_path: newPath,
      alt_text: input.altText,
      alt_text_gn: input.altTextGn,
      credit: input.credit || null,
      active: input.active,
      description: input.description,
      description_gn: input.descriptionGn,
      display_order: input.displayOrder,
      focal_x: input.focalX,
      focal_y: input.focalY,
      updated_by: userId,
    };
    const query = input.previousStoragePath
      ? requireSupabaseClient()
          .from("site_media")
          .update(values)
          .eq("municipality_id", input.municipalityId)
          .eq("slot_code", input.slotCode)
      : requireSupabaseClient()
          .from("site_media")
          .insert({ ...values, created_by: userId });
    const { error } = await query;
    if (error) {
      await removeImage(newPath);
      throw new Error("No se pudo guardar la imagen institucional.");
    }
    if (input.previousStoragePath !== newPath) {
      await removeImage(input.previousStoragePath);
    }
  },

  async saveMediaMetadata(input: SiteMediaInput, userId: string): Promise<void> {
    const { error } = await requireSupabaseClient()
      .from("site_media")
      .update({
        label: input.label,
        label_gn: input.labelGn,
        alt_text: input.altText,
        alt_text_gn: input.altTextGn,
        credit: input.credit || null,
        active: input.active,
        description: input.description,
        description_gn: input.descriptionGn,
        display_order: input.displayOrder,
        focal_x: input.focalX,
        focal_y: input.focalY,
        updated_by: userId,
      })
      .eq("municipality_id", input.municipalityId)
      .eq("slot_code", input.slotCode);
    if (error) throw new Error("No se pudieron guardar los textos de la imagen.");
  },

  async deleteMedia(item: SiteMedia): Promise<void> {
    const { error } = await requireSupabaseClient()
      .from("site_media")
      .delete()
      .eq("id", item.id);
    if (error) throw new Error("No se pudo retirar la imagen.");
    await removeImage(item.storagePath);
  },

  async listManagedNews(municipalityId: string): Promise<ManagedNews[]> {
    const { data, error } = await requireSupabaseClient()
      .from("news")
      .select(
        "id, slug, title, title_gn, excerpt, excerpt_gn, body, body_gn, category, category_gn, status, published_at, importance, image_path, image_alt, image_alt_gn, image_credit, source_url",
      )
      .eq("municipality_id", municipalityId)
      .order("importance", { ascending: false })
      .order("updated_at", { ascending: false });
    if (error) throw new Error("No se pudieron cargar las noticias administradas.");
    return rows(data).map(mapManagedNews);
  },

  async saveNews(input: NewsEditorInput, userId: string, image?: File): Promise<void> {
    const newPath = image
      ? await uploadImage(input.municipalityId, "news", image)
      : input.previousImagePath || null;
    const values = {
      municipality_id: input.municipalityId,
      slug: input.slug,
      title: input.title,
      title_gn: input.titleGn,
      excerpt: input.excerpt,
      excerpt_gn: input.excerptGn,
      body: input.body,
      body_gn: input.bodyGn,
      category: input.category,
      category_gn: input.categoryGn,
      status: input.status,
      published_at:
        input.status === "published"
          ? new Date(input.publishedAt || Date.now()).toISOString()
          : null,
      importance: input.importance,
      image_path: newPath,
      image_alt: newPath ? input.imageAlt : null,
      image_alt_gn: newPath ? input.imageAltGn : null,
      image_credit: input.imageCredit || null,
      source_url: input.sourceUrl || null,
      updated_by: userId,
    };
    const query = input.id
      ? requireSupabaseClient().from("news").update(values).eq("id", input.id)
      : requireSupabaseClient()
          .from("news")
          .insert({ ...values, created_by: userId });
    const { error } = await query;
    if (error) {
      if (image) await removeImage(newPath);
      throw new Error(
        error.code === "23505"
          ? "Ya existe una noticia con ese identificador."
          : "No se pudo guardar la noticia.",
      );
    }
    if (image && input.previousImagePath && input.previousImagePath !== newPath) {
      await removeImage(input.previousImagePath);
    }
  },

  async deleteNews(item: ManagedNews): Promise<void> {
    const { error } = await requireSupabaseClient()
      .from("news")
      .delete()
      .eq("id", item.id);
    if (error) throw new Error("No se pudo eliminar la noticia.");
    await removeImage(item.imagePath);
  },
};

